/**
 * Lightweight markdown-to-HTML renderer with bidirectional text support.
 * Handles: headers, tables, bold, italic, inline code, IPA/phonetic notation,
 * paragraphs, horizontal rules, lists, blockquotes.
 *
 * RTL documents (Arabic, Hebrew, Persian) get per-line direction detection
 * via dir="auto" on every block element, so Arabic lines render RTL and
 * romanization/English lines render LTR within the same document.
 *
 * No external dependencies.
 */

function escapeHtml(text: string): string {
  return text
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');
}

let _isRtlDoc = false;

const RTL_COUNT_RE = /[؀-ۿݐ-ݿࢠ-ࣿﭐ-﷿ﹰ-﻿֐-׿]/g;

function detectRtl(text: string): boolean {
  return (text.match(RTL_COUNT_RE) || []).length > 50;
}

function renderInline(text: string): string {
  let result = escapeHtml(text);
  // Restore safe inline HTML tags that escapeHtml converted to entities.
  // Only em/strong/code/br — no arbitrary HTML, no attributes.
  result = result.replace(/&lt;(\/?)(em|strong|code)&gt;/gi, '<$1$2>');
  result = result.replace(/&lt;br\s*\/?&gt;/gi, '<br/>');
  // Markdown inline formatting (after safe-tag restore so both syntaxes work)
  result = result.replace(/\*\*(.+?)\*\*/g, '<strong>$1</strong>');
  result = result.replace(/\*(.+?)\*/g, '<em>$1</em>');
  result = result.replace(/`([^`]+)`/g, '<code>$1</code>');
  return result;
}

function dirAttr(): string {
  return _isRtlDoc ? ' dir="auto"' : '';
}

function parseTable(tableLines: string[]): string {
  if (tableLines.length < 2) return '';

  const parseCells = (line: string): string[] => {
    const trimmed = line.replace(/^\|/, '').replace(/\|$/, '');
    return trimmed.split('|').map((c) => c.trim());
  };

  const firstLine = tableLines[0];
  if (!firstLine) return '';
  const headerCells = parseCells(firstLine);

  const bodyRows: string[][] = [];
  for (let i = 2; i < tableLines.length; i++) {
    const row = tableLines[i];
    if (row) {
      bodyRows.push(parseCells(row));
    }
  }

  let html = '<div class="overflow-x-auto my-4"><table class="min-w-full text-sm">';
  html += '<thead><tr>';
  for (const cell of headerCells) {
    html += `<th${dirAttr()} class="px-3 py-2 text-left font-medium text-gray-300 border-b border-white/10">${renderInline(cell)}</th>`;
  }
  html += '</tr></thead>';

  html += '<tbody>';
  for (const row of bodyRows) {
    html += '<tr class="border-b border-white/5">';
    for (let ci = 0; ci < Math.max(row.length, headerCells.length); ci++) {
      const cell = row[ci] ?? '';
      html += `<td${dirAttr()} class="px-3 py-2 text-gray-400">${renderInline(cell)}</td>`;
    }
    html += '</tr>';
  }
  html += '</tbody></table></div>';

  return html;
}

function isTableSeparator(line: string): boolean {
  return /^\|[\s\-:|]+\|$/.test(line.trim()) || /^[\s\-:|]+\|[\s\-:|]+/.test(line.trim());
}

function isTableRow(line: string): boolean {
  const trimmed = line.trim();
  return trimmed.startsWith('|') && trimmed.endsWith('|') && trimmed.includes('|');
}

function getLine(lines: string[], idx: number): string {
  return lines[idx] ?? '';
}

export function renderMarkdown(source: string): string {
  const isRtl = detectRtl(source);
  _isRtlDoc = isRtl;
  const lines = source.split('\n');
  const blocks: string[] = [];
  let i = 0;

  while (i < lines.length) {
    const line = getLine(lines, i);
    const trimmed = line.trim();

    if (trimmed === '') {
      i++;
      continue;
    }

    // Horizontal rule
    if (/^-{3,}$/.test(trimmed) || /^\*{3,}$/.test(trimmed)) {
      blocks.push('<hr class="border-white/10 my-6" />');
      i++;
      continue;
    }

    // Headers
    if (trimmed.startsWith('#')) {
      const match = trimmed.match(/^(#{1,6})\s+(.+)$/);
      if (match) {
        const lvl = match[1]?.length ?? 3;
        const text = match[2] ?? '';
        const sizes: Record<number, string> = {
          1: 'text-3xl font-heading font-bold text-gray-100 mt-8 mb-4',
          2: 'text-2xl font-heading font-bold text-gray-100 mt-8 mb-3',
          3: 'text-xl font-heading font-semibold text-gray-200 mt-6 mb-2',
          4: 'text-lg font-body font-semibold text-gray-200 mt-4 mb-2',
          5: 'text-base font-body font-medium text-gray-300 mt-3 mb-1',
          6: 'text-sm font-body font-medium text-gray-400 mt-2 mb-1',
        };
        const cls = sizes[lvl] ?? sizes[3] ?? '';
        blocks.push(
          `<h${lvl}${dirAttr()} class="${cls}">${renderInline(text)}</h${lvl}>`,
        );
        i++;
        continue;
      }
      blocks.push(`<p${dirAttr()} class="text-gray-400 leading-relaxed my-2">${renderInline(trimmed)}</p>`);
      i++;
      continue;
    }

    // Blockquote
    if (trimmed.startsWith('>')) {
      const quoteLines: string[] = [];
      while (i < lines.length) {
        const ql = getLine(lines, i).trim();
        if (ql.startsWith('>')) {
          quoteLines.push(ql.replace(/^>\s?/, ''));
          i++;
        } else {
          break;
        }
      }
      blocks.push(
        `<blockquote${dirAttr()} class="border-l-4 border-teal-500/40 pl-4 my-4 text-gray-400 italic">${quoteLines.map(l => `<p${dirAttr()} class="my-0.5">${renderInline(l)}</p>`).join('')}</blockquote>`,
      );
      continue;
    }

    // Table
    if (isTableRow(trimmed)) {
      const tableLines: string[] = [];
      while (i < lines.length) {
        const tl = getLine(lines, i).trim();
        if (isTableRow(tl) || isTableSeparator(tl)) {
          tableLines.push(tl);
          i++;
        } else {
          break;
        }
      }
      if (tableLines.length >= 2) {
        blocks.push(parseTable(tableLines));
      }
      continue;
    }

    // Unordered list
    if (/^[-*+]\s/.test(trimmed)) {
      const listItems: string[] = [];
      while (i < lines.length) {
        const ul = getLine(lines, i).trim();
        if (/^[-*+]\s/.test(ul)) {
          listItems.push(ul.replace(/^[-*+]\s/, ''));
          i++;
        } else {
          break;
        }
      }
      let html = '<ul class="list-disc list-inside space-y-1 my-3 text-gray-400">';
      for (const item of listItems) {
        html += `<li${dirAttr()}>${renderInline(item)}</li>`;
      }
      html += '</ul>';
      blocks.push(html);
      continue;
    }

    // Ordered list
    if (/^\d+\.\s/.test(trimmed)) {
      const listItems: string[] = [];
      while (i < lines.length) {
        const ol = getLine(lines, i).trim();
        if (/^\d+\.\s/.test(ol)) {
          listItems.push(ol.replace(/^\d+\.\s/, ''));
          i++;
        } else {
          break;
        }
      }
      let html = '<ol class="list-decimal list-inside space-y-1 my-3 text-gray-400">';
      for (const item of listItems) {
        html += `<li${dirAttr()}>${renderInline(item)}</li>`;
      }
      html += '</ol>';
      blocks.push(html);
      continue;
    }

    // Paragraph handling — differs for RTL vs LTR documents
    if (_isRtlDoc) {
      // Each line is its own paragraph so dir="auto" can set the correct
      // base direction per line (Arabic = RTL, romanization/English = LTR).
      // Joining lines destroys this — the BiDi algorithm can't recover
      // direction changes within a single <p>.
      blocks.push(
        `<p dir="auto" class="text-gray-400 leading-relaxed my-1">${renderInline(trimmed)}</p>`,
      );
      i++;
    } else {
      const paraLines: string[] = [];
      while (i < lines.length) {
        const pl = getLine(lines, i).trim();
        if (
          pl === '' ||
          pl.startsWith('#') ||
          pl.startsWith('>') ||
          isTableRow(pl) ||
          /^-{3,}$/.test(pl) ||
          /^[-*+]\s/.test(pl) ||
          /^\d+\.\s/.test(pl)
        ) {
          break;
        }
        paraLines.push(pl);
        i++;
      }
      if (paraLines.length > 0) {
        blocks.push(
          `<p class="text-gray-400 leading-relaxed my-2">${renderInline(paraLines.join(' '))}</p>`,
        );
      }
    }
  }

  const html = blocks.join('\n');
  if (isRtl) {
    return `<div class="rtl-doc">${html}</div>`;
  }
  return html;
}
